import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Wallet, ArrowRight, Loader2, AlertCircle, ExternalLink, CheckCircle2, Clock, RefreshCw } from "lucide-react";

/**
 * v23.4 — Driver payouts dashboard panel.
 * Shows Stripe Connect status + live balance + payout history with deep-link to
 * the Stripe Express dashboard for bank management / payout schedule changes.
 */
export default function DriverPayoutsPanel() {
  const [status, setStatus] = React.useState(null);
  const [balance, setBalance] = React.useState(null);
  const [payouts, setPayouts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [opening, setOpening] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, b, p] = await Promise.all([
        apiClient.get("/driver/stripe/status").then((r) => r.data),
        apiClient.get("/driver/stripe/balance").then((r) => r.data),
        apiClient.get("/driver/stripe/payouts").then((r) => r.data),
      ]);
      setStatus(s); setBalance(b); setPayouts(p.payouts || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load payout data");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openExpressDashboard = async () => {
    setOpening(true);
    setError("");
    try {
      const { data } = await apiClient.post("/driver/stripe/login-link");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't open Stripe dashboard");
    } finally {
      setOpening(false);
    }
  };

  const startOnboarding = async () => {
    setOpening(true);
    setError("");
    try {
      const { data } = await apiClient.post("/driver/stripe/onboard-link");
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start onboarding");
      setOpening(false);
    }
  };

  const fmt = (pence) => {
    const v = (pence || 0) / 100;
    return v.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
  };
  const fmtDate = (epoch) => {
    if (!epoch) return "—";
    try { return new Date(epoch * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; }
  };

  if (loading) {
    return (
      <section className="mt-16" data-testid="driver-payouts-loading">
        <div className="inline-flex items-center gap-2 text-sm text-[#475569]"><Loader2 size={16} className="animate-spin"/> Loading payouts&hellip;</div>
      </section>
    );
  }

  const connected = !!status?.connected;
  const fullyOnboarded = connected && status?.charges_enabled && status?.payouts_enabled && status?.details_submitted;

  return (
    <section className="mt-16" data-testid="driver-payouts-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Wallet className="text-[#1E4D55]"/> Payouts &amp; bank
          </h2>
          <p className="text-sm text-[#475569] max-w-xl mt-1">
            Track your Stripe Connect balance, see when payouts hit your bank, and manage payout schedule + bank details inside Stripe Express.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="payouts-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E4D55] hover:text-[#0A0A0A]"
        >
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="payouts-error">
          <AlertCircle size={12} className="mt-0.5"/> {error}
        </div>
      )}

      {/* Not connected yet */}
      {!connected && (
        <div className="bg-white border border-[#0A0A0A]/10 p-6" data-testid="payouts-not-connected">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#F5A623]">Action required</p>
          <h3 className="font-display mt-2 text-xl font-bold text-[#0A0A0A]">Connect your bank to get paid.</h3>
          <p className="mt-2 text-sm text-[#475569]">
            Raanah pays into your bank via Stripe Express. Onboarding takes 3 minutes — you&apos;ll need your sort code, account number, ID, and address.
          </p>
          <button
            onClick={startOnboarding}
            disabled={opening}
            data-testid="payouts-onboard-btn"
            className="mt-4 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold disabled:opacity-60"
          >
            {opening ? <Loader2 size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
            Start Stripe onboarding
          </button>
        </div>
      )}

      {/* Connected — show status grid + balance + payouts */}
      {connected && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-testid="payouts-balance-grid">
            <BalanceCard
              label="Available"
              hint="Ready to pay out to your bank"
              amount={fmt(balance?.available_pence || 0)}
              accent="#5BB247"
              testid="balance-available"
            />
            <BalanceCard
              label="Pending"
              hint="Clearing from recent jobs"
              amount={fmt(balance?.pending_pence || 0)}
              accent="#F5A623"
              testid="balance-pending"
            />
            <BalanceCard
              label="Connect status"
              hint={fullyOnboarded ? "Fully verified — ready" : "Finish remaining steps"}
              amount={fullyOnboarded ? "Active" : "In progress"}
              accent={fullyOnboarded ? "#5BB247" : "#F5A623"}
              testid="balance-status"
              text
            />
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 p-5 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="payouts-actions-row">
            <div>
              <p className="font-display font-bold text-[#0A0A0A]">Manage bank &amp; payout schedule</p>
              <p className="text-xs text-[#475569] mt-1">Add/update bank · change payout frequency · view tax forms — all in Stripe Express.</p>
            </div>
            <button
              onClick={openExpressDashboard}
              disabled={opening}
              data-testid="payouts-express-btn"
              className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              {opening ? <Loader2 size={14} className="animate-spin"/> : <ExternalLink size={14}/>}
              Open Stripe Express
            </button>
          </div>

          {!fullyOnboarded && (
            <div className="bg-[#F5A623]/10 border border-[#F5A623]/40 p-3 mb-4 text-xs text-[#7A4F0F]" data-testid="payouts-onboarding-warning">
              You&apos;ve started onboarding but Stripe still needs:{" "}
              {!status?.details_submitted && <span className="font-semibold">your business/personal details · </span>}
              {!status?.charges_enabled && <span className="font-semibold">payments capability · </span>}
              {!status?.payouts_enabled && <span className="font-semibold">payout capability · </span>}
              <button onClick={startOnboarding} className="underline font-bold ml-1">Resume onboarding</button>
            </div>
          )}

          {/* Payout history */}
          <div className="bg-white border border-[#0A0A0A]/10 overflow-hidden" data-testid="payouts-history">
            <div className="px-5 py-3 border-b border-[#0A0A0A]/10">
              <p className="font-display font-bold text-[#0A0A0A]">Recent payouts to your bank</p>
            </div>
            {(!payouts || payouts.length === 0) ? (
              <p className="px-5 py-6 text-sm text-[#475569] italic" data-testid="payouts-empty">No payouts yet — once you complete jobs and Stripe schedules a transfer, they&apos;ll appear here.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[#475569] bg-[#F5F5F5]">
                  <tr>
                    <th className="px-5 py-2 text-left">Amount</th>
                    <th className="px-5 py-2 text-left">Status</th>
                    <th className="px-5 py-2 text-left">Method</th>
                    <th className="px-5 py-2 text-left">Arrival date</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} data-testid={`payout-row-${p.id}`} className="border-t border-[#0A0A0A]/5">
                      <td className="px-5 py-3 font-mono font-bold text-[#0A0A0A]">{fmt(p.amount_pence)}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          {p.status === "paid" ? <CheckCircle2 size={12} className="text-[#5BB247]"/> : <Clock size={12} className="text-[#F5A623]"/>}
                          <span className="capitalize">{p.status}</span>
                        </span>
                      </td>
                      <td className="px-5 py-3 text-[#475569] capitalize">{p.method?.replace("_", " ") || "—"}</td>
                      <td className="px-5 py-3 font-mono text-[#0A0A0A]">{fmtDate(p.arrival_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}


function BalanceCard({ label, hint, amount, accent, testid, text }) {
  return (
    <div className="bg-white border border-[#0A0A0A]/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="inline-block" style={{ width: 8, height: 8, background: accent }} />
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#475569]">{label}</p>
      </div>
      <p className={`font-display font-black text-[#0A0A0A] mt-3 leading-tight ${text ? "text-2xl" : "text-3xl"}`}>{amount}</p>
      <p className="text-xs text-[#475569] mt-1">{hint}</p>
    </div>
  );
}
